import { useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { ArrowLeft, Send, Check, X, MessageSquarePlus, GitBranch, ExternalLink, FileSignature } from "lucide-react";

const INR = (n = 0) => new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 }).format(n);

const STATUS_STYLES = {
  draft: "bg-zinc-100 text-zinc-700",
  sent: "bg-blue-50 text-blue-700 border-blue-200",
  under_negotiation: "bg-amber-50 text-amber-700 border-amber-200",
  approved: "bg-emerald-50 text-emerald-700 border-emerald-200",
  project_created: "bg-emerald-100 text-emerald-800 border-emerald-300",
  rejected: "bg-red-50 text-red-700 border-red-200",
  expired: "bg-zinc-100 text-zinc-500",
};

export default function QuotationDetail() {
  const { qid } = useParams();
  const nav = useNavigate();
  const qc = useQueryClient();
  const [negotiatePrice, setNegotiatePrice] = useState("");
  const [negotiateNote, setNegotiateNote] = useState("");
  const [revisionNote, setRevisionNote] = useState("");
  const [rejectReason, setRejectReason] = useState("");

  const { data: q, isLoading } = useQuery({
    queryKey: ["quotation", qid],
    queryFn: async () => (await api.get(`/quotations/${qid}`)).data,
  });
  const { data: activity = [] } = useQuery({
    queryKey: ["qt-activity", qid],
    queryFn: async () => (await api.get(`/quotations/${qid}/activity`)).data,
  });

  const act = useMutation({
    mutationFn: ({ action, body = {} }) => api.post(`/quotations/${qid}/${action}`, body),
    onSuccess: (_, vars) => {
      toast.success(`${vars.action.replace(/_/g, " ")} · done`);
      qc.invalidateQueries({ queryKey: ["quotation", qid] });
      qc.invalidateQueries({ queryKey: ["qt-activity", qid] });
      if (vars.action === "approve") {
        // give the user a beat to see the toast, then bounce them to the created project.
        setTimeout(() => qc.invalidateQueries({ queryKey: ["quotations"] }), 400);
      }
    },
    onError: (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)),
  });

  if (isLoading) return <div className="p-8 text-sm text-zinc-500">Loading…</div>;
  if (!q) return <div className="p-8 text-sm text-red-600">Quotation not found.</div>;

  const canEdit = ["draft", "sent", "under_negotiation"].includes(q.status) && !q.archived;

  return (
    <div className="max-w-[1400px] mx-auto px-8 py-6 space-y-6" data-testid="quotation-detail">
      {/* Header */}
      <div className="flex items-start justify-between gap-6">
        <div className="flex items-start gap-3">
          <button onClick={() => nav("/app/quotations")} className="p-2 rounded-md hover:bg-zinc-100" data-testid="back-btn">
            <ArrowLeft className="w-4 h-4 text-zinc-500" />
          </button>
          <div>
            <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold">Quotation</div>
            <h1 className="font-heading text-2xl font-extrabold tracking-tight">{q.number}</h1>
            <div className="text-sm text-zinc-600 mt-1">{q.title}</div>
          </div>
        </div>
        <div className="text-right space-y-2">
          <Badge className={`capitalize border ${STATUS_STYLES[q.status] || ""}`} data-testid="status-badge">{q.status.replace(/_/g, " ")}</Badge>
          <div className="text-2xl font-heading font-extrabold tracking-tight" data-testid="final-price">{INR(q.final_price || q.suggested_price || 0)}</div>
          <div className="text-[11px] text-zinc-500 font-mono uppercase tracking-widest">{q.pricing_model?.replace(/_/g, " ")}</div>
        </div>
      </div>

      {/* Action bar */}
      <div className="flex flex-wrap items-center gap-2">
        {["draft", "under_negotiation"].includes(q.status) && (
          <Button onClick={() => act.mutate({ action: "send" })} size="sm" className="gap-1.5" data-testid="btn-send"><Send className="w-3.5 h-3.5" /> Send</Button>
        )}
        {["draft", "sent", "under_negotiation"].includes(q.status) && (
          <Button onClick={() => act.mutate({ action: "approve" })} size="sm" variant="outline" className="gap-1.5 border-emerald-300 text-emerald-700 hover:bg-emerald-50" data-testid="btn-approve"><Check className="w-3.5 h-3.5" /> Approve → create project</Button>
        )}
        {["draft", "sent", "under_negotiation"].includes(q.status) && (
          <Button
            onClick={() => act.mutate({ action: "reject", body: { reason: rejectReason || "Rejected" } })}
            size="sm" variant="outline"
            className="gap-1.5 border-red-300 text-red-700 hover:bg-red-50"
            data-testid="btn-reject"
          ><X className="w-3.5 h-3.5" /> Reject</Button>
        )}
        {q.project_id && (
          <Link to={`/app/projects/${q.project_id}`} className="text-xs text-emerald-700 hover:underline inline-flex items-center gap-1" data-testid="link-project">
            Open Project <ExternalLink className="w-3 h-3" />
          </Link>
        )}
        {q.handover_id && (
          <Link to="/app/handover-inbox" className="text-xs text-emerald-700 hover:underline inline-flex items-center gap-1" data-testid="link-handover">
            Open Handover <ExternalLink className="w-3 h-3" />
          </Link>
        )}
      </div>

      {/* Tabs */}
      <Tabs defaultValue="overview" className="space-y-4">
        <TabsList data-testid="detail-tabs">
          <TabsTrigger value="overview" data-testid="tab-overview">Overview</TabsTrigger>
          <TabsTrigger value="scope" data-testid="tab-scope">Scope &amp; Sheets</TabsTrigger>
          <TabsTrigger value="pricing" data-testid="tab-pricing">Pricing</TabsTrigger>
          <TabsTrigger value="negotiation" data-testid="tab-negotiation">Negotiation</TabsTrigger>
          <TabsTrigger value="revisions" data-testid="tab-revisions">Revisions</TabsTrigger>
          <TabsTrigger value="activity" data-testid="tab-activity">Activity</TabsTrigger>
        </TabsList>

        <TabsContent value="overview">
          <div className="grid md:grid-cols-3 gap-4">
            <div className="border border-zinc-200 rounded-md bg-white p-4">
              <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold mb-2">Client</div>
              <div className="text-sm font-medium">{q.client?.name || "—"}</div>
              <div className="text-xs text-zinc-500">{q.client?.legal_name}</div>
            </div>
            <div className="border border-zinc-200 rounded-md bg-white p-4">
              <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold mb-2">Valid till</div>
              <div className="text-sm font-mono">{q.valid_till || "—"}</div>
            </div>
            <div className="border border-zinc-200 rounded-md bg-white p-4">
              <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold mb-2">Suggested / Negotiated / Final</div>
              <div className="text-xs font-mono space-y-0.5">
                <div>Suggested: {INR(q.suggested_price || 0)}</div>
                <div>Negotiated: {q.negotiated_price != null ? INR(q.negotiated_price) : "—"}</div>
                <div className="font-bold text-zinc-900">Final: {INR(q.final_price || 0)}</div>
              </div>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="scope">
          <div className="space-y-4">
            <div className="border border-zinc-200 rounded-md bg-white p-5">
              <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold mb-2">Scope of Work</div>
              <div className="text-sm whitespace-pre-wrap text-zinc-800">{q.scope_of_work || <span className="text-zinc-400">No scope captured.</span>}</div>
            </div>
            <div className="border border-zinc-200 rounded-md bg-white p-5">
              <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold mb-2">Drawing / Sheet List</div>
              {q.drawing_sheet_list?.length ? (
                <table className="w-full text-xs">
                  <thead className="text-[10px] uppercase tracking-widest text-zinc-500">
                    <tr><th className="text-left py-1">Code</th><th className="text-left py-1">Name</th><th className="text-left py-1">Discipline</th><th className="text-right py-1">Qty</th></tr>
                  </thead>
                  <tbody className="divide-y divide-zinc-100">
                    {q.drawing_sheet_list.map((s) => (
                      <tr key={s.id}><td className="py-1 font-mono">{s.code || "—"}</td><td className="py-1">{s.name}</td><td className="py-1 capitalize">{s.discipline}</td><td className="py-1 text-right font-mono">{s.qty}</td></tr>
                    ))}
                  </tbody>
                </table>
              ) : <div className="text-xs text-zinc-400">No sheet list provided.</div>}
            </div>
          </div>
        </TabsContent>

        <TabsContent value="pricing">
          <div className="border border-zinc-200 rounded-md bg-white p-5">
            <div className="flex items-center justify-between mb-4">
              <div>
                <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold">Pricing Model</div>
                <div className="font-mono text-sm">{q.pricing_model?.replace(/_/g, " ")}</div>
              </div>
              <div className="text-right">
                <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold">Suggested Price</div>
                <div className="font-mono text-lg font-bold">{INR(q.suggested_price || 0)}</div>
              </div>
            </div>

            {q.pricing_model === "hourly" && (
              <div>
                <table className="w-full text-xs">
                  <thead className="text-[10px] uppercase tracking-widest text-zinc-500">
                    <tr>
                      <th className="text-left py-1">Description</th><th className="text-left py-1">Discipline</th>
                      <th className="text-right py-1">Hours</th><th className="text-right py-1">Rate</th>
                      <th className="text-right py-1">Margin %</th><th className="text-right py-1">Subtotal</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-zinc-100">
                    {(q.lines || []).map((l) => {
                      const sub = l.hours * l.hourly_rate * (1 + (l.margin_pct || 0) / 100);
                      return (
                        <tr key={l.id}>
                          <td className="py-1.5">{l.description}</td>
                          <td className="py-1.5 capitalize">{l.discipline}</td>
                          <td className="py-1.5 text-right font-mono">{l.hours}</td>
                          <td className="py-1.5 text-right font-mono">₹{l.hourly_rate}</td>
                          <td className="py-1.5 text-right font-mono">{l.margin_pct}%</td>
                          <td className="py-1.5 text-right font-mono">{INR(sub)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                <div className="flex items-center justify-end gap-6 mt-4 text-xs text-zinc-600">
                  <div>Estimated hours: <span className="font-mono text-zinc-900">{q.hourly?.estimated_hours || 0}</span></div>
                  <div>Blended rate: <span className="font-mono text-zinc-900">₹{q.hourly?.blended_rate || 0}/hr</span></div>
                </div>
              </div>
            )}
            {q.pricing_model === "fixed_project" && (
              <div className="text-sm">Agreed project price: <span className="font-mono">{INR(q.fixed?.agreed_price || 0)}</span></div>
            )}
            {q.pricing_model === "monthly_resource" && (
              <div className="text-sm space-y-1">
                <div>{q.monthly?.num_resources} resource(s) × {INR(q.monthly?.monthly_rate || 0)} / month × {q.monthly?.duration_months} months</div>
                <div className="font-mono font-bold">Total: {INR(q.monthly?.total || 0)}</div>
              </div>
            )}
            {q.pricing_model === "per_drawing_sheet" && (
              <div className="text-sm space-y-1">
                <div>{q.per_sheet?.num_sheets} sheet(s) × {INR(q.per_sheet?.rate_per_sheet || 0)} / sheet</div>
                <div className="font-mono font-bold">Total: {INR(q.per_sheet?.total || 0)}</div>
              </div>
            )}
            {q.pricing_model === "lump_sum" && (
              <div className="text-sm">Lump-sum agreed: <span className="font-mono">{INR(q.lump_sum?.agreed_price || 0)}</span></div>
            )}
            {q.pricing_model === "custom" && (
              <div className="text-sm space-y-2">
                <div>Agreed: <span className="font-mono">{INR(q.custom?.agreed_price || 0)}</span></div>
                <div className="text-xs text-zinc-600 whitespace-pre-wrap">{q.custom?.notes}</div>
              </div>
            )}

            <div className="border-t border-zinc-100 mt-6 pt-4 text-xs text-zinc-600 flex justify-between">
              <div>Tax {q.tax_percent || 0}%: <span className="font-mono">{INR(q.tax_amount || 0)}</span></div>
              <div className="font-mono font-bold text-zinc-900">Final: {INR(q.final_price || 0)}</div>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="negotiation">
          <div className="space-y-4">
            {canEdit && (
              <div className="border border-zinc-200 rounded-md bg-white p-4">
                <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold mb-3">Add negotiation entry</div>
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <Label>Revised price</Label>
                    <Input type="number" value={negotiatePrice} onChange={(e) => setNegotiatePrice(e.target.value)} className="font-mono" data-testid="neg-price" />
                  </div>
                  <div className="col-span-2">
                    <Label>Note</Label>
                    <Input value={negotiateNote} onChange={(e) => setNegotiateNote(e.target.value)} placeholder="Client asked for 10% discount on the interior scope…" data-testid="neg-note" />
                  </div>
                </div>
                <div className="mt-3 flex justify-end">
                  <Button
                    size="sm"
                    onClick={() => {
                      act.mutate({
                        action: "negotiate",
                        body: {
                          price: negotiatePrice ? Number(negotiatePrice) : null,
                          note: negotiateNote,
                        },
                      });
                      setNegotiatePrice(""); setNegotiateNote("");
                    }}
                    data-testid="neg-submit"
                  ><MessageSquarePlus className="w-3.5 h-3.5 mr-1.5" /> Log negotiation</Button>
                </div>
              </div>
            )}
            <ol className="border-l border-zinc-200 pl-6 space-y-4" data-testid="neg-history">
              {(q.negotiation_history || []).slice().reverse().map((n, i) => (
                <li key={`${n.at || "no-at"}-${i}`} className="relative">
                  <span className="absolute -left-[29px] top-1.5 w-2.5 h-2.5 rounded-full bg-amber-500" />
                  <div className="text-xs text-zinc-500 font-mono">{n.at?.slice(0, 19).replace("T", " ")} · {n.by_name}</div>
                  <div className="text-sm text-zinc-800">
                    {n.price != null && <span className="font-mono font-bold">{INR(n.price)}</span>}
                    {n.price != null && n.note && " · "}
                    {n.note}
                  </div>
                </li>
              ))}
              {!q.negotiation_history?.length && <li className="text-xs text-zinc-500 -ml-6">No negotiations logged yet.</li>}
            </ol>

            {/* M2-A8 · Approval History (append-only) */}
            {(q.approval_history?.length || 0) > 0 && (
              <div className="mt-8">
                <h4 className="text-xs uppercase tracking-widest text-zinc-500 font-semibold mb-3">Approval history</h4>
                <ol className="border-l border-emerald-200 pl-6 space-y-4" data-testid="approval-history">
                  {q.approval_history.map((h, i) => {
                    const dot = {
                      submitted_for_approval: "bg-blue-500",
                      resubmitted: "bg-blue-500",
                      returned_for_modification: "bg-rose-500",
                      approved: "bg-emerald-500",
                      sent_to_client: "bg-violet-500",
                    }[h.event] || "bg-zinc-400";
                    const label = {
                      submitted_for_approval: "Submitted for approval",
                      resubmitted: "Resubmitted for approval",
                      returned_for_modification: "Returned for modification",
                      approved: "Approved",
                      sent_to_client: "Sent to client",
                    }[h.event] || h.event;
                    return (
                      <li key={`${h.event}-${h.at || i}`} className="relative" data-testid={`history-${h.event}`}>
                        <span className={`absolute -left-[29px] top-1.5 w-2.5 h-2.5 rounded-full ${dot}`} />
                        <div className="text-xs text-zinc-500 font-mono">{h.at?.slice(0, 19).replace("T", " ")} · {h.actor_name}</div>
                        <div className="text-sm font-medium text-zinc-800">{label}</div>
                        {h.reason && <div className="text-xs text-rose-700 mt-0.5 italic">Reason: {h.reason}</div>}
                        {h.note && <div className="text-xs text-zinc-600 mt-0.5">Note: {h.note}</div>}
                        {h.negotiated_price != null && <div className="text-xs text-zinc-600 mt-0.5">Negotiated price: {INR(h.negotiated_price)}</div>}
                      </li>
                    );
                  })}
                </ol>
              </div>
            )}
          </div>
        </TabsContent>

        <TabsContent value="revisions">
          <div className="space-y-4">
            {canEdit && (
              <div className="border border-zinc-200 rounded-md bg-white p-4">
                <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold mb-3">Snapshot current version</div>
                <div className="grid gap-3">
                  <Input placeholder="What changed in this revision?" value={revisionNote} onChange={(e) => setRevisionNote(e.target.value)} data-testid="rev-note" />
                </div>
                <div className="mt-3 flex justify-end">
                  <Button size="sm" onClick={() => { act.mutate({ action: "create-revision", body: { note: revisionNote } }); setRevisionNote(""); }} data-testid="rev-submit">
                    <GitBranch className="w-3.5 h-3.5 mr-1.5" /> Save as R{q.revision_version}
                  </Button>
                </div>
              </div>
            )}
            <ol className="border-l border-zinc-200 pl-6 space-y-4" data-testid="rev-history">
              {(q.revisions || []).slice().reverse().map((r) => (
                <li key={r.version} className="relative">
                  <span className="absolute -left-[29px] top-1.5 w-2.5 h-2.5 rounded-full bg-zinc-700" />
                  <div className="text-xs text-zinc-500 font-mono">R{r.version} · {r.at?.slice(0, 19).replace("T", " ")} · {r.by_name}</div>
                  <div className="text-sm text-zinc-800">{r.changes_summary}</div>
                  <div className="text-[11px] font-mono text-zinc-500">
                    Suggested {INR(r.snapshot?.suggested_price || 0)} · Negotiated {r.snapshot?.negotiated_price != null ? INR(r.snapshot.negotiated_price) : "—"}
                  </div>
                </li>
              ))}
              {!q.revisions?.length && <li className="text-xs text-zinc-500 -ml-6">No revisions yet.</li>}
            </ol>
          </div>
        </TabsContent>

        <TabsContent value="activity">
          <ol className="border-l border-zinc-200 pl-6 space-y-3" data-testid="activity-log">
            {activity.map((a) => (
              <li key={a.id} className="relative">
                <span className="absolute -left-[29px] top-1.5 w-2.5 h-2.5 rounded-full bg-zinc-400" />
                <div className="text-xs text-zinc-500 font-mono">{a.at?.slice(0, 19).replace("T", " ")}</div>
                <div className="text-sm">{a.actor_name} · <span className="text-zinc-600">{a.action}</span></div>
              </li>
            ))}
            {activity.length === 0 && <li className="text-xs text-zinc-500 -ml-6">No activity yet.</li>}
          </ol>
        </TabsContent>
      </Tabs>
    </div>
  );
}
